import posthog from 'posthog-js';
import { POSTHOG_API_KEY, POSTHOG_HOST } from '../config';

let posthogInitialized = false;

/**
 * Initialize PostHog analytics
 * Call this once when your app starts
 */
export const initPostHog = () => {
  // Only initialize if API key is provided
  if (!POSTHOG_API_KEY) {
    console.warn('PostHog API key not found. Analytics will not be tracked.');
    return null;
  }

  // Prevent double initialization
  if (posthogInitialized) {
    return posthog;
  }

  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      opt_in_site_apps: true,
      // Enable automatic pageview tracking
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog initialized successfully');
        }
      },
      // Capture pageviews automatically
      capture_pageview: true,
      // Capture pageleaves automatically
      capture_pageleave: true,
      // Enable session recording with improved configuration
      disable_session_recording: false,
      session_recording: {
        // Record cross-origin iframes (set to false if not using iframes)
        recordCrossOriginIframes: false,
      },
      // Enable autocapture for clicks, form submissions, etc.
      autocapture: true,
      // Persist user across sessions
      persistence: 'localStorage+cookie',
      // Cross-subdomain cookie support
      cross_subdomain_cookie: false,
      // Secure cookie in production
      secure_cookie: window.location.protocol === 'https:',
    });

    posthogInitialized = true;
    return posthog;
  } catch (error) {
    console.error('Failed to initialize PostHog:', error);
    return null;
  }
};

/**
 * Identify a user in PostHog
 * Call this when a user logs in or when you have user information
 * @param {string} userId - Unique user identifier
 * @param {object} userProperties - Additional user properties (email, name, etc.)
 */
export const identifyUser = (userId, userProperties = {}) => {
  if (!posthogInitialized || !POSTHOG_API_KEY) {
    return;
  }

  try {
    posthog.identify(userId, userProperties);
  } catch (error) {
    console.error('Failed to identify user in PostHog:', error);
  }
};

/**
 * Reset user identification
 * Call this when a user logs out
 */
export const resetUser = () => {
  if (!posthogInitialized || !POSTHOG_API_KEY) {
    return;
  }

  try {
    posthog.reset();
  } catch (error) {
    console.error('Failed to reset user in PostHog:', error);
  }
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {object} properties - Additional event properties
 */
export const trackEvent = (eventName, properties = {}) => {
  if (!posthogInitialized || !POSTHOG_API_KEY) {
    return;
  }

  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    console.error('Failed to track event in PostHog:', error);
  }
};

/**
 * Get the PostHog instance (for advanced usage)
 */
export const getPostHog = () => {
  return posthogInitialized ? posthog : null;
};

export default posthog;


